import { useCallback, useEffect, useState } from 'react';
import { PAGINATION } from '@erpgold/shared';
import { storeService, Store, StoreFilters } from '../services/store.service';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const useStores = (initialFilters: StoreFilters = {}) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<StoreFilters>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters,
  });
  const [meta, setMeta] = useState<PaginationMeta>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  });

  const fetchStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await storeService.getStores(filters);
      setStores(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch stores'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  return { stores, loading, error, meta, filters, setFilters, refetch: fetchStores };
};

export const useStoreDetail = (id: string) => {
  const [store, setStore] = useState<Store | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchStore = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await storeService.getStoreById(id);
      setStore(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch store'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStore();
  }, [fetchStore]);

  return { store, loading, error, refetch: fetchStore };
};
